/**
 * The Patient Access manager's daily report.
 *
 * The date picker is the only scope control; everything below reads the selected
 * Pacific day. The by-coordinator table (BAR-2516) and the hourly call log
 * (BAR-2517) land beneath the hero tiles.
 */

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { downloadAttachmentBlob } from '@/services/attachments';
import { buildCiDailyReportCsv, pacDailyReportCsvFilename } from '@bariendo/lib/pac-daily-report-csv';
import { Alert, Button, Container, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconChevronLeft, IconChevronRight, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactElement, useState } from 'react';
import { CallLog } from './CallLog';
import { CoordinatorTable } from './CoordinatorTable';
import { HeroMetrics } from './HeroMetrics';
import {
  PAC_REPORT_TIMEZONE,
  formatPacificDay,
  shiftPacificDay,
  todayInPacific,
  usePacDailyReport,
} from './usePacDailyReport';

dayjs.extend(utc);
dayjs.extend(timezone);

function ReportHeader({
  date,
  onDateChange,
  onExport,
}: {
  date: string;
  onDateChange: (next: string) => void;
  /** Absent until the day's report has loaded — there is nothing to export yet. */
  onExport?: () => void;
}): ReactElement {
  const isToday = date === todayInPacific();

  return (
    <Group justify="space-between" align="center" wrap="wrap">
      <Group gap="xs" align="baseline">
        <Title order={1}>PAC daily report</Title>
        <Text size="sm" c="dimmed">
          all times PT
        </Text>
      </Group>

      <Group gap="xs">
        <Button
          variant="default"
          size="xs"
          leftSection={<IconDownload size={14} />}
          disabled={!onExport}
          onClick={onExport}
        >
          Export
        </Button>
        <Button variant="default" size="xs" disabled={isToday} onClick={() => onDateChange(todayInPacific())}>
          Today
        </Button>
        <Button
          variant="default"
          size="xs"
          aria-label="Previous day"
          onClick={() => onDateChange(shiftPacificDay(date, -1))}
        >
          <IconChevronLeft size={14} />
        </Button>
        <Text size="sm" fw={500} style={{ minWidth: 150, textAlign: 'center' }}>
          {formatPacificDay(date)}
        </Text>
        <Button
          variant="default"
          size="xs"
          aria-label="Next day"
          // A future day has no report to show.
          disabled={isToday}
          onClick={() => onDateChange(shiftPacificDay(date, 1))}
        >
          <IconChevronRight size={14} />
        </Button>
      </Group>
    </Group>
  );
}

function AuthorizedCiManagerDashboardPage(): ReactElement {
  const [date, setDate] = useState<string>(todayInPacific());
  const { report, availability, patientNames, practitionerNames, loading, error } = usePacDailyReport(date);

  const exportCsv = (): void => {
    if (!report) {
      return;
    }

    const csv = buildCiDailyReportCsv(report, {
      date,
      timezone: PAC_REPORT_TIMEZONE,
      patientNames,
      practitionerNames,
      formatTime: (instant) => dayjs(instant).tz(PAC_REPORT_TIMEZONE).format('h:mma'),
    });

    // Deliberately no analytics event: every row carries a patient name or a
    // phone number, and the count alone would not be worth the risk of somebody
    // later adding the contents to it.
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    downloadAttachmentBlob(url, pacDailyReportCsvFilename(date));
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <ReportHeader date={date} onDateChange={setDate} onExport={report ? exportCsv : undefined} />

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Couldn't load the report" color="red">
            {error}
          </Alert>
        )}

        {loading && !report && (
          <Paper withBorder p="xl" radius="md">
            <Group justify="center">
              <Loader size="sm" />
            </Group>
          </Paper>
        )}

        {/* Zeros because nothing was counted, not because the line was quiet. The
            two look identical on the tiles, so the difference has to be said. */}
        {report && !report.intakeBranchConfigured && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="No Care Intake phone-tree branch configured"
            color="yellow"
          >
            No menu branch is configured as Care Intake&rsquo;s, so no calls can be attributed to the team and every
            call figure below reads zero.
          </Alert>
        )}

        {/* An empty day renders the report with zeros rather than a spinner or an
            error — "nobody called" is a real answer. */}
        {report && <HeroMetrics report={report} />}

        {/* Nobody's calls, so they are in none of the ratios above — but they are
            the biggest population on the line, and a manager cannot act on a
            number nobody shows them. */}
        {report && report.noSelection.total > 0 && (
          <Text size="sm" c="dimmed">
            {report.noSelection.total} {report.noSelection.total === 1 ? 'caller' : 'callers'} heard the menu and chose
            nothing — {report.noSelection.forwarded} forwarded on, {report.noSelection.abandoned} hung up. Not counted
            as intake calls.
          </Text>
        )}
        {report && <CoordinatorTable report={report} availability={availability} />}
        {report && <CallLog report={report} patientNames={patientNames} practitionerNames={practitionerNames} />}
      </Stack>
    </Container>
  );
}

export function PacManagerDashboardPage(): ReactElement {
  const {
    currentUserPermissions: { canViewCiManagerDashboard },
  } = useCurrentUser();

  // Gate on the opt-in permission rather than the Patient Access role: a coordinator
  // holds that role themselves, and this report is about them. What the page can
  // actually read is enforced server-side by the access policy (BAR-2520).
  if (!canViewCiManagerDashboard) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          This page is only accessible to administrators and users granted Patient Access manager access.
        </Alert>
      </Container>
    );
  }

  return <AuthorizedCiManagerDashboardPage />;
}
